import { computed } from '@ember/object';
import lodashSum from 'lodash.sum';
import moment from 'moment-timezone';
import { Grouping, groupTimestamps } from 'cio/utils/metrics/resolution';
import { BLUE_500, GREEN_500, INK_500, INK_900, PLUM_500, PURPLE_700, RASPBERRY_500, YELLOW_500 } from './colors';
import { MetricDataPoint, Periods, getGroupingRanges } from 'cio/utils/metrics/common';
import {
  CalculatedMeasure,
  calculateMeasure,
  MeasureType,
  JOURNEY_METRICS_MEASURE_DEFINITIONS,
} from 'cio/utils/metrics/measure';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { TimeRange } from 'cio/utils/time';

// January 1st 2019, midnight
// Journey metrics were only backfilled to start of that date, so we have to
// restrict the dates to disallow selecting anything before that
export const MIN_START_TIME_UTC = 1546300800;

export const MAX_MONTH_COUNT = 12;

export type APIJourneyMetricsMeasure =
  | 'started'
  | 'activated'
  | 'messaged'
  | 'finished'
  | 'exited_early'
  | 'never_activated'
  | 'converted';

export type JourneyMetricsMeasure =
  | 'started'
  | 'activated'
  | 'messaged'
  | 'in_workflow'
  | 'finished'
  | 'never_activated'
  | 'exited_early'
  | 'converted';

export const METRICS: APIJourneyMetricsMeasure[] = [
  'started',
  'activated',
  'messaged',
  'finished',
  'exited_early',
  'never_activated',
  'converted',
];

export const AVAILABLE_MEASURES: JourneyMetricsMeasure[] = [
  'started',
  'activated',
  'messaged',
  'in_workflow',
  'finished',
  'never_activated',
  'exited_early',
  'converted',
];

export type UnderscoredMeasure =
  | '_started'
  | '_activated'
  | '_messaged'
  | '_finished'
  | '_exited_early'
  | '_never_activated'
  | '_converted';

export const UNDERSCORED_MEASURES: Record<APIJourneyMetricsMeasure, UnderscoredMeasure> = {
  started: '_started',
  activated: '_activated',
  messaged: '_messaged',
  finished: '_finished',
  exited_early: '_exited_early',
  never_activated: '_never_activated',
  converted: '_converted',
};

export const MEASURE_LABELS: Record<JourneyMetricsMeasure, string> = {
  started: 'Triggered',
  activated: 'Started',
  messaged: 'Messaged',
  finished: 'Completed',
  exited_early: 'Exited early',
  never_activated: 'Filtered out',
  converted: 'Converted',
  in_workflow: 'In progress',
};

export const JOURNEY_MEASURE_DEFINITIONS: Record<JourneyMetricsMeasure, string> = {
  started: "A person has matched the trigger, but they haven't actually started their journey.",
  activated: 'A person has matched the trigger and completed a non-delay action.',
  messaged: 'A person has reached any Email, SMS, Push, Slack, or Webhook message.',
  in_workflow: 'A person is waiting in a delay in the workflow.',
  finished: 'A person has moved through the entire campaign workflow.',
  never_activated:
    'A person started a journey but exited before reaching an action—a message, attribute change, or entering a new segment.',
  exited_early: 'A person exited after a grace period, a manual removal, or a profile deletion.',
  converted: 'A person has met your conversion criteria.',
};

export const DATA_CAMPAIGN_JOURNEY_MEASURE_DEFINITIONS: Record<JourneyMetricsMeasure, string> = {
  started: "Data arrived, but it hasn't actually started its journey.",
  activated: 'Data arrived and completed a non-delay action.',
  messaged: 'Data arrived and reached any Slack or Webhook message.',
  in_workflow: 'Data is waiting in a delay in the workflow.',
  finished: 'Data has moved through the entire campaign workflow.',
  never_activated: 'Data started a journey but exited before reaching a Slack or Webhook message.',
  exited_early: 'Data exited a journey after a manual removal.',
  converted: 'N/A',
};

export const MEASURE_DENOMINATORS: Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null> = {
  started: null,
  activated: 'started',
  messaged: 'started',
  finished: 'started',
  exited_early: 'started',
  never_activated: 'started',
  converted: 'activated',
  in_workflow: 'started',
};

export const MEASURE_COLORS: Record<string, { name: string; value: string }> = {
  started: {
    name: 'ink-900',
    value: INK_900,
  },
  activated: {
    name: 'ink-500',
    value: INK_500,
  },
  in_workflow: {
    name: 'yellow-500',
    value: YELLOW_500,
  },
  finished: {
    name: 'green-500',
    value: GREEN_500,
  },
  never_activated: {
    name: 'plum-500',
    value: PLUM_500,
  },
  exited_early: {
    name: 'blue-500',
    value: BLUE_500,
  },
  converted: {
    name: 'purple-700',
    value: PURPLE_700,
  },
  messaged: {
    name: 'raspberry-500',
    value: RASPBERRY_500,
  },
};

export function computedMeasure(measure: JourneyMetricsMeasure) {
  let measureDefinition = JOURNEY_METRICS_MEASURE_DEFINITIONS[measure];

  let dependentKeys: string[] = [];

  if (measureDefinition.isComputed) {
    dependentKeys = [...measureDefinition.dependentKeys];
  } else {
    dependentKeys = [measureDefinition.valueKey];
  }

  if (measureDefinition.denominator) {
    dependentKeys.push(measureDefinition.denominator);
  }

  return computed(
    ...dependentKeys.map((m: APIJourneyMetricsMeasure) => UNDERSCORED_MEASURES[m]),
    'range.{start,end}',
    'grouping',
    // @ts-ignore the definition for computed doesn't seem to like it when we try
    // and destructure the dependent keys
    function (this: Metric) {
      return calculateMeasure(
        measure as JourneyMetricsMeasure,
        MeasureType.JourneyMetrics,
        [this],
        this.rawMetrics,
        this.groupedMetrics,
      );
    },
  ) as Ember.ComputedProperty<CalculatedMeasure>;
}

// Given start and end moment objects, calculates the months between those dates
// (including the dates). For example, a range from 2019-01-01 to 2019-06-01
// would return:
// ['2019-01', '2019-02', '2019-03', '2019-04', '2019-05']
export function monthsBetweenDates(startTime: moment.Moment, endTime: moment.Moment): string[] {
  let startYear = startTime.year();
  let endYear = endTime.year();
  let yearDiff = endYear - startYear;

  let startMonthIndex = startTime.month();
  let endMonthIndex = endTime.month();

  let monthDiff = yearDiff * 12 + (endMonthIndex - startMonthIndex);
  let months: string[] = [];

  for (let i = 0; i <= monthDiff; i++) {
    months.push(moment(startTime).add(i, 'months').format('YYYY-MM'));
  }

  return months;
}

export class JourneyMetrics {
  constructor(params: {
    data: Record<APIJourneyMetricsMeasure, number[]>;
    timestampedData: Record<APIJourneyMetricsMeasure, MetricDataPoint[]>;
    startTime: moment.Moment;
    endTime: moment.Moment;
    grouping: Grouping;
    timezone: string;
  }) {
    this.data = params.data;

    this.startTime = params.startTime;
    this.endTime = params.endTime;

    this.grouping = params.grouping;

    this.timezone = params.timezone;

    METRICS.forEach((measure) => {
      if (params?.timestampedData?.[measure]) {
        this[UNDERSCORED_MEASURES[measure]] = params.timestampedData[measure];
      }
    });
  }

  data: Record<string, number[]>;

  @tracked grouping: Grouping = Grouping.Daily;

  timezone: string;

  startTime: moment.Moment;
  endTime: moment.Moment;

  get range() {
    return {
      start: this.startTime,
      end: this.endTime,
    };
  }

  // These contain all the raw data returned from the API
  @tracked _started: MetricDataPoint[] = [];
  @tracked _activated: MetricDataPoint[] = [];
  @tracked _messaged: MetricDataPoint[] = [];
  @tracked _finished: MetricDataPoint[] = [];
  @tracked _exited_early: MetricDataPoint[] = [];
  @tracked _never_activated: MetricDataPoint[] = [];
  @tracked _converted: MetricDataPoint[] = [];

  get rawMetrics() {
    let metrics: Record<any, MetricDataPoint[]> = {};

    METRICS.forEach((measure: APIJourneyMetricsMeasure) => {
      metrics[measure] = this[UNDERSCORED_MEASURES[measure]];
    });

    return metrics;
  }

  get groupedMetrics() {
    if (this.range == null) {
      return {};
    }

    let metrics: Record<any, MetricDataPoint[]> = {};

    let startTimestamp = (this.range.start?.unix() ?? 0) * 1000;
    let endTimestamp = (this.range.end?.unix() ?? 0) * 1000;

    METRICS.forEach((measure: APIJourneyMetricsMeasure) => {
      let trimmedData = [...this[UNDERSCORED_MEASURES[measure]]].filter((p) => p.x >= startTimestamp);

      if (!this.rangeLengths || this.rangeLengths.length === 0) {
        metrics[measure] = trimmedData.filter((p) => p.x <= endTimestamp);
        return;
      }

      let trimmedValues = trimmedData.map((p) => p.y);

      let timestamps = groupTimestamps(this.range as TimeRange, this.grouping, this.timezone);

      metrics[measure] = this.rangeLengths.map((len: number, i: number) => {
        return {
          x: timestamps[i],
          y: lodashSum(trimmedValues.splice(0, len)),
        };
      });
    });

    return metrics;
  }

  @computed('grouping', 'range.{start,end}')
  get rangeLengths() {
    if (!this.range) {
      return [];
    }

    return getGroupingRanges(this.range, Periods.DAYS, this.grouping, this.timezone);
  }

  @computedMeasure('started') started!: CalculatedMeasure;
  @computedMeasure('activated') activated!: CalculatedMeasure;
  @computedMeasure('messaged') messaged!: CalculatedMeasure;
  @computedMeasure('finished') finished!: CalculatedMeasure;
  @computedMeasure('exited_early') exited_early!: CalculatedMeasure;
  @computedMeasure('never_activated') never_activated!: CalculatedMeasure;
  @computedMeasure('converted') converted!: CalculatedMeasure;
  @computedMeasure('in_workflow') in_workflow!: CalculatedMeasure;
}
